import { useMemo } from 'react';
import type { Staff, WorkingHours } from '@shared/schema';
import { DAYS_OF_WEEK } from '@/lib/utils';

/** Swagger: HQ-only rows may have id `0`; there is no persisted hours row for that path. */
export function isPlaceholderStaffId(id: string | number | null | undefined): boolean {
  if (id == null) return false;
  const s = String(id).trim();
  if (s === "") return false;
  /** `Number("") === 0` is true in JS — do not treat empty / missing string ids as placeholders. */
  return s === "0" || Number(s) === 0;
}

/** Day schedule rows used by Settings → Working hours (team) and Admin Staff edit dialog. */
export type StaffScheduleDayRow = {
  dayOfWeek: number;
  isActive: boolean;
  startTime: string;
  endTime: string;
};

/** Build 7-day schedule from `WorkingHours` rows (same defaults as settings UI). */
export function workingHoursToStaffScheduleDays(memberHours: WorkingHours[]): StaffScheduleDayRow[] {
  return DAYS_OF_WEEK.map((_, i) => {
    const existing = memberHours.find((wh) => wh.dayOfWeek === i);
    return existing
      ? {
          dayOfWeek: i,
          isActive: existing.isActive !== false,
          startTime: existing.startTime,
          endTime: existing.endTime,
        }
      : { dayOfWeek: i, isActive: i >= 1 && i <= 5, startTime: '09:00', endTime: '17:00' };
  });
}

/**
 * Initial staff + hours from GET `/admin/staff` (`StaffResponse.workingHours` in swagger).
 * Per-staff GET `/.../staff/{id}/hours` is used only after PATCH hours (see Admin Settings save handler).
 */
export function useStaffListWorkingHours(staffList: Staff[] | undefined): {
  hoursByStaffIndex: WorkingHours[][] | undefined;
  isLoadingHours: boolean;
} {
  const hoursByStaffIndex = useMemo(() => {
    if (!staffList?.length) return [];
    return staffList.map((member) => member.workingHours ?? []);
  }, [staffList]);

  if (!staffList?.length) {
    return { hoursByStaffIndex: [], isLoadingHours: false };
  }

  return { hoursByStaffIndex, isLoadingHours: false };
}
